// @flow
import _ from 'lodash';
import NumberUtils from 'common/utils/NumberUtils';
import TimeUtils from 'common/utils/TimeUtils';
import convert from 'convert-units';
import isFastEqual from 'react-fast-compare';
import type { CustomField } from 'common/models/internal/EntitySettings';

import type { List, Map } from 'immutable';

export type Omit<FromType, Properties: { [string]: 1 }> = $Exact<
    $Diff<
        FromType,
        $ObjMap<Properties, <K, V>(k: K, v: V) => $ElementType<FromType, K>>
    >
>;

export default class DataUtils {
    static isEmpty(value: any): boolean {
        if (_.isNumber(value)) {
            return !_.isFinite(value); // if finite, return false. Covers NaN
        }

        return _.isEmpty(value);
    }

    static isDeepEmpty(value: any): boolean {
        if (!_.isObject(value)) {
            // added explicit check for boolean value
            return DataUtils.isBoolean(value)
                ? false
                : DataUtils.isEmpty(value);
        } else {
            let isEmpty = true;
            for (const key in value) {
                isEmpty = DataUtils.isDeepEmpty(value[key]);
                if (!isEmpty) {
                    break;
                }
            }
            if (!isEmpty) return false;
        }
        return true;
    }

    static convertMetersToKms(
        distance: number,
        { decimals = 0, roundOff = true }
    ) {
        return NumberUtils.formatNumber(distance / 1000, roundOff, decimals);
    }

    static convertSecondsToHours(
        time: number,
        options: { roundOff: boolean } = { roundOff: true }
    ) {
        return NumberUtils.padLeft(
            TimeUtils.humanizeSeconds(time, {
                roundOff: options.roundOff,
                units: ['h'],
                format: {
                    h: () => ''
                }
            })
        );
    }

    static convertTo({
        value,
        sourceUnit,
        convertToUnit,
        roundOff,
        padLeft,
        decimals
    }: {
        value?: number,
        sourceUnit?: Object,
        convertToUnit?: Object,
        decimals?: number,
        roundOff?: boolean
    }) {
        if (DataUtils.isEmpty(value)) {
            return null;
        }

        let convertedValue = value;
        if (sourceUnit && convertToUnit) {
            convertedValue = convert(value)
                .from(sourceUnit.abbr)
                .to(convertToUnit.abbr);
        }
        if (roundOff) {
            convertedValue = NumberUtils.round(convertedValue, decimals);
        }
        if (padLeft) {
            convertedValue = NumberUtils.padLeft(convertedValue);
        }
        return convertedValue;
    }

    static validateCustomProperties(
        fields: List<CustomField>,
        values: Map<string, string>
    ) {
        return fields.every(field => {
            // Every field should return undefined if non issues
            return !field.validate(values.get(field.key));
        });
    }

    static isValidJSON(data) {
        let jsonString;
        if (typeof data === 'string') {
            jsonString = data;
        } else {
            /**
             * If data is not string type, then convert (JSON.stringify)
             */
            jsonString = JSON.stringify(data);
        }
        // now data is string, JSON.parse should pass incase of json object
        try {
            const isJSON = JSON.parse(jsonString);
            /**
             * Check for null (null is a valid json, typeof null = object)
             * but "null" is itself falsey
             */
            if (isJSON && typeof isJSON === 'object') {
                return true;
            }
            return false;
        } catch (e) {
            // Parse throws error; Not a JSON
            return false;
        }
    }

    static isBoolean(value: any): boolean {
        return _.isBoolean(value);
    }

    /**
     * @DEPRECATED `isFastEqual` should be used instead for performance
     */
    static isEqual(value1: any, value2: any): boolean {
        return _.isEqual(value1, value2);
    }

    static isFastEqual(value1: any, value2: any): boolean {
        return isFastEqual(value1, value2);
    }

    static omit<FromType: Object, Properties>(
        Obj: FromType,
        properties: { [string]: 1 }
    ): Omit<FromType, Properties> {
        return _.omit(Obj, Object.keys(properties));
    }
}

export { DataUtils };
